import React, { Component } from 'react';
import { Location } from 'history';
import moment from 'moment';
import history from '../../../history';
import { ROOT_URL_V2 } from '../../../config';
import { executeHttpRequest } from '../../../epics/api';
import { withPushFiltersToUrl } from '../../operations/common/decorators';
import { TPageNotification } from '../../../types';
import PayrollAdminFilters from './PayrollAdminFilters';
import PayrollAdminTable, { TAgencyTimesheetSummary } from './PayrollAdminTable';

type TPayrollAdminProps = {
    location: Location;
    notification: TPageNotification;
    onFilterChange: (id: string, value: any) => void;
};

type TPayrollAdminState = {
    content: TAgencyTimesheetSummary[];
    fetching: boolean;
    error?: string;
};

class PayrollAdmin extends Component<TPayrollAdminProps, TPayrollAdminState> {
    state: TPayrollAdminState = {
        content: [],
        fetching: false,
        error: undefined,
    };

    componentDidMount() {
        const { search } = history.location;
        const currentUrlParams = new URLSearchParams(search);
        if (search) {
            this.load(currentUrlParams);
        } else {
            const paramString = new URLSearchParams(
                this.getDefaultQueryParams() as Record<string, string>,
            ).toString();
            history.push({
                pathname: '/payments/payroll-admin/',
                search: paramString,
            });
        }
    }

    componentDidUpdate(prevProps: TPayrollAdminProps) {
        if (prevProps.location?.search !== this.props.location?.search) {
            this.load(new URLSearchParams(history.location.search));
        }
    }

    getDefaultQueryParams = () => ({
        bookings_after: moment()
            .startOf('isoWeek')
            .startOf('day')
            .subtract(1, 'week')
            .toISOString(),
        bookings_before: moment()
            .endOf('isoWeek')
            .endOf('day')
            .subtract(1, 'week')
            .toISOString(),
    });

    load = async(searchParams: URLSearchParams) => {
        const startDate = searchParams.get('bookings_after');
        const endDate = searchParams.get('bookings_before');
        if (!startDate || !endDate) {
            this.props.notification.error('Error', 'Please select a date range.');
            return;
        }

        const formattedStartDate = moment(startDate);
        const formattedEndDate = moment(endDate);
        if (!formattedStartDate.isValid() || !formattedEndDate.isValid()) {
            return;
        }

        this.setState({ fetching: true, error: undefined });
        try {
            const response = await executeHttpRequest(
                `timesheets/agency-timesheet-summary/?start_date=${encodeURIComponent(
                    formattedStartDate.format('YYYY-MM-DD'),
                )}&end_date=${encodeURIComponent(
                    formattedEndDate.format('YYYY-MM-DD'),
                )}`,
            );
            this.setState({
                content: response.data?.content || [],
                fetching: false,
                error: undefined,
            });
        } catch (error) {
            this.setState({
                fetching: false,
                error: 'Failed to load agency summary.',
            });
            this.props.notification.error(
                'Error',
                'Failed to load agency summary.',
            );
        }
    };

    handleDownload = (agencyId: number) => {
        const searchParams = new URLSearchParams(history.location.search);
        const startDate = searchParams.get('bookings_after');
        const endDate = searchParams.get('bookings_before');
        const formattedStartDate = moment(startDate);
        const formattedEndDate = moment(endDate);
        if (!formattedStartDate.isValid() || !formattedEndDate.isValid()) {
            this.props.notification.error(
                'Error',
                'Please select a valid date range.',
            );
            return;
        }

        const downloadUrl = `${ROOT_URL_V2}timesheets/?agency_id=${encodeURIComponent(
            String(agencyId),
        )}&start_date=${encodeURIComponent(
            formattedStartDate.format('YYYY-MM-DD'),
        )}&end_date=${encodeURIComponent(
            formattedEndDate.format('YYYY-MM-DD'),
        )}&format=csv`;

        window.location.href = downloadUrl;
    };

    render() {
        return (
            <div className="table-container">
                <PayrollAdminFilters
                    onFilterChange={this.props.onFilterChange}
                    reloadData={() =>
                        this.load(new URLSearchParams(history.location.search))
                    }
                />
                <PayrollAdminTable
                    loading={this.state.fetching}
                    content={this.state.content}
                    errorMsg={this.state.error}
                    onDownload={this.handleDownload}
                />
            </div>
        );
    }
}

export default withPushFiltersToUrl(PayrollAdmin);
